/**
 * Admin: user role management.
 *
 * Wires `PATCH /users/{user_id}/role` (real BE endpoint #4). Admin-only —
 * gated by RequireRole.
 *
 * Scope (current): self-service role management. The admin can change
 * their *own* role and provide a reason. The user UUID is taken from
 * `/users/me` and never exposed in the UI — admins should never have to
 * paste UUIDs.
 *
 * Scope (future): when the BE adds `GET /users`, this screen rebuilds
 * as a searchable user list with click-to-edit. The PATCH hook stays
 * the same; only the picker UX changes.
 */

import { isApiError } from "@compliance/domain";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCircleCheckFilled,
  IconInfoCircle,
  IconUserShield,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { type UserRoleValue, usePatchUserRole } from "../api/hooks";
import type { UserAccessView, UserResponse } from "../api/types";
import { useUser } from "../auth";
import { formatApiError } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";

const ROLE_OPTIONS: { value: UserRoleValue; label: string }[] = [
  { value: "operations", label: "Operations" },
  { value: "reviewer", label: "Compliance Reviewer" },
  { value: "admin", label: "Compliance Admin" },
];

export function AdminUsersPage() {
  // Canonical user, straight from the AuthProvider that owns the query rather
  // than a second fetcher on the same cache key. The UUID lives here — we never
  // ask the admin to paste it.
  const me = useUser();
  const patch = usePatchUserRole();
  const queryClient = useQueryClient();

  const [roleSelection, setRoleSelection] = useState<UserRoleValue | null>(null);
  const [reason, setReason] = useState("");
  const [lastResult, setLastResult] = useState<UserAccessView | null>(null);

  // The selector defaults to the current role and only diverges once the admin
  // picks a different one — derived from `me`, so there's no effect to sync.
  const role = roleSelection ?? me?.role ?? null;

  const reasonValid = reason.trim().length >= 8;
  const roleChanged = me !== null && role !== null && role !== me.role;
  const canSubmit = !!me && roleChanged && reasonValid && !patch.isPending;
  const demotingSelf = roleChanged && role !== "admin";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !role || !me) return;
    try {
      const updated = await patch.mutateAsync({
        userId: me.id,
        role,
        reason: reason.trim(),
      });
      setLastResult(updated);
      notifications.show({
        title: "Role updated",
        message: `You are now ${updated.role}.`,
        color: "teal",
        icon: <IconCircleCheckFilled size={18} />,
        autoClose: 3500,
      });
      // Refresh the canonical user so RequireRole re-evaluates — if the
      // admin just demoted themselves out of admin, the next
      // navigation will redirect to /403.
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
      setReason("");
    } catch (err) {
      notifications.show({
        title: "Role update failed",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Group gap="sm" align="center">
          <ThemeIcon variant="light" color="indigo" size={36} radius="md">
            <IconUserShield size={20} />
          </ThemeIcon>
          <Title order={2}>User role management</Title>
        </Group>
        <Text c="dimmed" size="sm" maw={680}>
          Manage your own role. Every change writes an audit row with the reason. Searching and
          editing other users will land once the backend ships{" "}
          <Text span ff="monospace" size="sm">
            GET /users
          </Text>
          .
        </Text>
      </Stack>

      {me ? (
        <Paper p="lg" radius="md" withBorder>
          <Group gap="md" wrap="nowrap">
            <Avatar size={56} radius="xl" color="indigo">
              {initialsFor(me)}
            </Avatar>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Group gap="sm" wrap="wrap">
                <Text fw={600}>{me.display_name ?? me.email}</Text>
                <Badge variant="light">{me.role}</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {me.email}
              </Text>
              <Text size="xs" c="dimmed">
                Status: {me.status}
              </Text>
            </Stack>
          </Group>
        </Paper>
      ) : (
        <Paper p="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed">
            Loading your profile…
          </Text>
        </Paper>
      )}

      <Card withBorder p="lg" radius="md" component="form" onSubmit={onSubmit}>
        <Stack gap="md">
          <Stack gap={4}>
            <Text fw={600} size="sm">
              Change your role
            </Text>
            <Text size="xs" c="dimmed">
              Promote or demote yourself. Useful for testing role-gated screens; in production this
              is normally how the admin onboards their own account.
            </Text>
          </Stack>

          <Select
            label="New role"
            value={role}
            onChange={(v) => setRoleSelection(v as UserRoleValue | null)}
            data={ROLE_OPTIONS}
            disabled={!me}
            required
          />

          <Textarea
            label="Reason"
            description="Recorded in the audit chain. Minimum 8 characters."
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder="e.g. Testing role-gated audit view as auditor"
            autosize
            minRows={2}
            maxRows={5}
            required
            error={reason && !reasonValid ? "Reason must be at least 8 characters" : null}
          />

          {demotingSelf ? (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">
                You&apos;re about to change your role away from{" "}
                <Text span fw={600}>
                  admin
                </Text>
                . After this succeeds you&apos;ll lose access to this screen until another admin (or
                the dev backend) promotes you back.
              </Text>
            </Alert>
          ) : null}

          {patch.error ? (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertCircle size={16} />}
              title={
                isApiError(patch.error) ? `Update failed (${patch.error.code})` : "Update failed"
              }
            >
              <Text size="sm">{(patch.error as Error).message}</Text>
              {isApiError(patch.error) ? (
                <Text size="xs" c="dimmed" mt={4} ff="monospace">
                  request_id {patch.error.requestId}
                </Text>
              ) : null}
            </Alert>
          ) : null}

          <Group justify="flex-end">
            <Button type="submit" disabled={!canSubmit} loading={patch.isPending} color="indigo">
              {roleChanged ? "Update role" : "No change"}
            </Button>
          </Group>
        </Stack>
      </Card>

      {lastResult ? (
        <Alert color="teal" variant="light" icon={<IconInfoCircle size={16} />}>
          <Stack gap={6}>
            <Text size="sm" fw={600}>
              Last response
            </Text>
            <Code block>{JSON.stringify(lastResult, null, 2)}</Code>
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  );
}

function initialsFor(user: UserResponse): string {
  const source = user.display_name ?? user.email.split("@")[0] ?? "";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return (source.slice(0, 2) || "??").toUpperCase();
}
